import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import {
    Avatar,
    Badge,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    Grid,
    Icon,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { Box } from '@mui/system';
import queryString from 'query-string';

import SideDrawer, { DRAWER_CONTENT_TYPES } from 'Lib/components/layout/components/SideDrawer';
import useApi from 'Lib/hooks/useApi';
import Dialog from 'Lib/muiComponents/dialog/Dialog';
import filterParameters from 'Lib/utilities/filterParameters';
import isEmpty from 'Lib/utilities/isEmpty';
import { screenSize } from 'Lib/utilities/screenSize';

const Mobile = ({ children, open, fullscreen, toggle }) => (
    <>
        <IconButton aria-label="open saved searches" onClick={toggle}>
            save_as
        </IconButton>
        <Dialog open={open} toggle={toggle} fullscreen={fullscreen}>
            <Typography sx={{ fontSize: theme => theme.typography.pxToRem(20), letterSpacing: '0.15px', mb: 4 }}>Saved Searches</Typography>
            {children}
        </Dialog>
    </>
);

const EmptyMessage = () => (
    <>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, width: '100%' }}>
            <Avatar sx={{ bgcolor: 'success.secondary', height: 96, width: 96 }}>
                <Badge overlap="circular" badgeContent={<Icon sx={{ color: 'success.main' }}>auto_awesome</Icon>}>
                    <Icon sx={{ color: 'success.main', fontSize: 56 }}>save</Icon>
                </Badge>
            </Avatar>
        </Box>
        <br />
        <Typography data-cy="table-saved-searches-empty" sx={{ textAlign: 'center' }}>
            It seems you haven’t saved any searches yet.
        </Typography>
    </>
);

const Content = ({ savedSearches, onClick }) => (
    <Box sx={{ p: 4 }}>
        <Typography sx={{ fontSize: theme => theme.typography.pxToRem(20), letterSpacing: '0.15px' }}>Saved Searches</Typography>
        {isEmpty(savedSearches) ? <EmptyMessage /> : <Searches savedSearches={savedSearches} onClick={onClick} />}
    </Box>
);

const Searches = ({ savedSearches, onClick }) => (
    <List disablePadding sx={{ mt: 2, mb: -2 }}>
        {savedSearches.slice(0, 4).map(({ savedList }) => (
            <ListItem key={savedList.savedListID} disablePadding disableGutters sx={{ mb: 1 }}>
                <ListItemButton disableGutters sx={{ borderRadius: 2, mx: -2, py: 0 }} onClick={() => onClick(savedList, false)}>
                    <ListItemIcon sx={{ mx: 2 }}>
                        <Icon>saved_search</Icon>
                    </ListItemIcon>
                    <ListItemText>{savedList.name}</ListItemText>
                </ListItemButton>
            </ListItem>
        ))}
    </List>
);

function SavedSearch(props) {
    const dispatch = useDispatch();
    const history = useHistory();
    const { mobile } = screenSize();

    const { setSavedSearches, toggleDrawer } = props.actions;
    const { createSavedList, deleteSavedList, updateSavedList } = props.api || {};

    const savedSearches = useSelector(state => state.ui.savedSearches);
    const { listTypeID } = useSelector(state => state.ui.table);
    const drawerContentTypeID = useSelector(state => state.ui.drawerContentTypeID);

    const [anchorElement, setAnchorElement] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState('');
    const [savedSearch, setSavedSearch] = useState(null);

    const createSearch = useApi(createSavedList);
    const deleteSearch = useApi(deleteSavedList);
    const updateSearch = useApi(updateSavedList);

    const handleSavedSearch = (savedList, sideDrawerEvent = true) => {
        history.push(savedList.filters);
        props.fetch();
        handleClose();
        toggleOpen();

        if (drawerContentTypeID && sideDrawerEvent) dispatch(toggleDrawer());
    };

    const handleDelete = (e, savedList) => {
        e.stopPropagation();
        setLoading(true);

        deleteSearch(savedList.savedListID)
            .then(() => {
                const updatedSearches = savedSearches.filter(search => search.savedList.savedListID != savedList.savedListID);
                dispatch(setSavedSearches(updatedSearches));
                setSavedSearch(null);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const handleEdit = (e, savedList) => {
        e.stopPropagation();
        setSavedSearch(savedList);
    };

    const handleClick = event => {
        setAnchorElement(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorElement(null);
    };

    const handleChange = e => {
        setValue(e.target.value);
    };

    const toggleOpen = () => setOpen(prevState => !prevState);

    const handleSave = editValue => {
        const { stringified } = filterParameters();
        const data = {
            name: editValue ? editValue : value,
            /*
				We care about storing everything besides page and it's associated number + ampersand &, examples below

				1. ?leaseStatusIDs=2&page=1&pageSize=15 --> removes '&page=1'; has ampersand before and after but only removes the first
				2. ?leaseStatusIDs=2&pageSize=15&page=5 --> removes '&page=5'; has ampersand before and removes it
				3. ?page=5&leaseStatusIDs=2&pageSize=15 --> remove 'page=5&'; has ampersand after and removes it
			*/
            filters: `?${stringified}`.replace(/page=\d+&?|&?page=\d+/, ''),
            listReportTypeID: listTypeID
        };

        setLoading(true);

        if (savedSearch) {
            updateSearch(savedSearch.savedListID, data)
                .then(response => {
                    const updatedSearches = savedSearches.map(search => {
                        if (search.savedList.savedListID == savedSearch.savedListID) return response.data;

                        return search;
                    });
                    dispatch(setSavedSearches(updatedSearches));

                    setSavedSearch(null);
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                });
        } else {
            createSearch(data)
                .then(response => {
                    const updatedSearches = [response.data, ...savedSearches];
                    dispatch(setSavedSearches(updatedSearches));

                    setLoading(false);
                    setValue('');
                    handleClose();
                })
                .catch(() => {
                    setLoading(false);
                });
        }
    };

    const openPopover = Boolean(anchorElement);
    const id = openPopover ? 'saved-searches' : undefined;

    const CardTitle = ({ editing, savedList }) => {
        const [editValue, setEditValue] = useState(savedList.name);

        if (editing)
            return (
                <Box display="flex" justifyContent="space-between">
                    <TextField
                        autoFocus
                        size="small"
                        value={editValue}
                        disabled={loading}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setEditValue(e.target.value)}
                        sx={{ flexGrow: 1, mb: 'auto' }}
                    />
                    <div>
                        <IconButton
                            disabled={loading}
                            sx={{ color: 'success.main', mt: -1, ml: 2 }}
                            onClick={e => {
                                handleSave(editValue);
                                e.stopPropagation();
                            }}
                        >
                            done
                        </IconButton>
                        <IconButton
                            disabled={loading}
                            edge="end"
                            sx={{ color: 'error.main', mt: -1 }}
                            onClick={e => {
                                setSavedSearch(null);
                                e.stopPropagation();
                            }}
                        >
                            cancel
                        </IconButton>
                    </div>
                </Box>
            );

        return <Typography component="div">{savedList.name}</Typography>;
    };

    const SavedSearches = () => {
        return savedSearches.map(({ savedList }) => {
            // Converts saved camelcase values to more legible text
            const filters = Object.keys(queryString.parse(savedList.filters))
                .filter(key => key !== 'pageSize') // Omit pageSize as this will always show
                .map(key => {
                    // Remove ID from keys like 'propertyTypeIDs' which will translate to 'Property Types'
                    const filter = key.replace(/[ID]/g, '').replace(/([A-Z])/g, ' $1');
                    const result = filter.charAt(0).toUpperCase() + filter.slice(1);

                    return result;
                })
                .join(' + ');
            const editing = savedSearch && savedSearch.savedListID == savedList.savedListID;

            return (
                <Card
                    key={savedList.savedListID}
                    sx={{
                        bgcolor: 'common.lightblue',
                        border: '1px solid #EAEAEA',
                        boxShadow: 'unset',
                        mb: 2,
                        '&:hover': { boxShadow: 1, cursor: 'pointer' }
                    }}
                    onClick={() => handleSavedSearch(savedList)}
                >
                    <CardHeader title={<CardTitle editing={editing} savedList={savedList} />} sx={{ pt: 3, pb: 2 }} />
                    <CardContent sx={{ pt: 1, pb: editing ? 4 : 0 }}>
                        <Typography
                            sx={{
                                color: '#545454',
                                fontSize: theme => theme.typography.pxToRem(12),
                                fontStyle: 'italic',
                                letterSpacing: '0.4px',
                                lineHeight: '20px'
                            }}
                        >
                            {filters}
                        </Typography>
                    </CardContent>
                    <CardActions disableSpacing sx={{ justifyContent: 'flex-end', p: 0 }}>
                        {!editing && (
                            <>
                                <Tooltip arrow title="Delete saved search" placement="bottom-end">
                                    <div>
                                        <IconButton disabled={loading} sx={{ py: 0 }} onClick={e => handleDelete(e, savedList)}>
                                            delete
                                        </IconButton>
                                    </div>
                                </Tooltip>
                                <Tooltip arrow title="Edit saved search" placement="bottom-end">
                                    <div>
                                        <IconButton disabled={loading} sx={{ py: 0 }} onClick={e => handleEdit(e, savedList)}>
                                            edit
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            </>
                        )}
                    </CardActions>
                </Card>
            );
        });
    };

    if (!listTypeID) return null;
    else if (props.inline)
        return (
            <Content
                savedSearches={savedSearches}
                onClick={filters => {
                    handleSavedSearch(filters);
                    props.handleClose();
                }}
            />
        );

    return mobile ? (
        <Mobile toggle={toggleOpen} open={open}>
            {isEmpty(savedSearches) ? (
                <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center">
                    <Grid item>
                        <EmptyMessage />
                        <TextField
                            value={value}
                            size="small"
                            placeholder="Enter name to save search"
                            onChange={handleChange}
                            sx={{ bgcolor: 'common.white', width: '-webkit-fill-available', mx: 4, mt: 4, mb: -2 }}
                        />
                        <Button
                            variant="primary"
                            disabled={!value || loading}
                            sx={{ textTransform: 'inherit', width: '-webkit-fill-available', mx: 4, mt: 4, mb: 2 }}
                            onClick={() => handleSave()}
                        >
                            {loading && <CircularProgress size={24} sx={{ mr: 2 }} />} Save this search
                        </Button>
                    </Grid>
                </Grid>
            ) : (
                <SavedSearches />
            )}
        </Mobile>
    ) : (
        <>
            <Tooltip
                arrow
                placement="bottom"
                title={
                    <Box data-cy="table-saved-searches-tooltip" component="div" sx={{ textAlign: 'center' }}>
                        View saved searches or
                        <br />
                        save a new search
                    </Box>
                }
            >
                <Box>
                    <IconButton data-cy="table-saved-searches-button" variant="regular" onClick={handleClick}>
                        save_as
                    </IconButton>
                </Box>
            </Tooltip>
            <Popover
                id={id}
                data-cy="table-saved-searches-popover"
                open={openPopover}
                anchorEl={anchorElement}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <Box sx={{ width: 316 }}>
                    <Content savedSearches={savedSearches} onClick={handleSavedSearch} />
                    <Divider sx={{ bgcolor: 'common.gray' }} />
                    <Box sx={{ bgcolor: 'common.offwhite', textAlign: 'center' }}>
                        <TextField
                            data-cy="table-saved-searches-input"
                            value={value}
                            size="small"
                            placeholder="Enter name to save search"
                            onChange={handleChange}
                            sx={{ bgcolor: 'common.white', width: '-webkit-fill-available', mx: 4, mt: 4, mb: -2 }}
                        />
                        <Button
                            data-cy="table-saved-searches-save"
                            variant="primary"
                            disabled={!value || loading}
                            sx={{ textTransform: 'inherit', width: '-webkit-fill-available', mx: 4, mt: 4, mb: 2 }}
                            onClick={() => handleSave()}
                        >
                            {loading && <CircularProgress size={24} sx={{ mr: 2 }} />} Save this search
                        </Button>
                        {!isEmpty(savedSearches) && (
                            <Button
                                variant="text"
                                sx={{
                                    color: 'link.main',
                                    textTransform: 'inherit',
                                    width: '-webkit-fill-available',
                                    mx: 4,
                                    mb: 2
                                }}
                                onClick={() => {
                                    dispatch(toggleDrawer(DRAWER_CONTENT_TYPES.SAVED_SEARCH));
                                    handleClose();
                                }}
                            >
                                View all saved searches
                            </Button>
                        )}
                    </Box>
                </Box>
            </Popover>
            <SideDrawer title="Saved Searches" drawerContentTypeID={DRAWER_CONTENT_TYPES.SAVED_SEARCH}>
                <SavedSearches />
            </SideDrawer>
        </>
    );
}

export default SavedSearch;
